/* RESPONSIBLE TEAM: team-ai-agent */
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';

/**
 * Parent route for /ask. Stays active across all child routes (index, chat, schedules),
 * so bridge event subscriptions registered here survive child route transitions.
 */
export default class AskRoute extends Route {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare router: RouterService;

  private unsubscribeKnowledgeHub?: () => void;
  private unsubscribeNavigate?: () => void;

  activate() {
    // Subscribe to knowledge-hub:open-content events from React.
    // This must live on the parent route (not the index) because navigating
    // to /ask/:chat_id deactivates the index route, which would unsubscribe.
    this.unsubscribeKnowledgeHub = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'knowledge-hub:open-content',
      (data: Record<string, unknown>) => {
        let contentId = data.contentId as number | string;
        let contentType = data.contentType as string;
        let editorMode = (data.editorMode as string) || 'view';

        if (editorMode === 'view') {
          this.knowledgeHubDrawerEditorService.openViewDrawer({
            activeContentId: contentId,
            activeContentType: contentType,
            newTab: false,
          });
        } else {
          this.knowledgeHubDrawerEditorService.openEditDrawer({
            activeContentId: contentId,
            activeContentType: contentType,
            newTab: false,
          });
        }
      },
    );

    // Subscribe to internal-agent:navigate events for non-knowledge-hub entity types.
    // These entities have dedicated pages rather than the knowledge hub drawer.
    this.unsubscribeNavigate = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'internal-agent:navigate',
      (data: Record<string, unknown>) => {
        let entityType = data.entityType as number;
        let entityId = data.entityId as number;

        switch (entityType) {
          case 159: // Guidance
            this.router.transitionTo('apps.app.automation.fin-ai-agent.guidance');
            break;
          case 184: // Procedure
            this.router.transitionTo(
              'apps.app.automation.fin-ai-agent.procedures.procedure',
              entityId,
            );
            break;
          case 5: // Conversation
            safeWindowOpen(
              this.router.urlFor('apps.app.inbox.inbox.conversation', entityId),
              '_blank',
            );
            break;
          case 113: // Data Connector
            // TODO: Add route navigation once data connector detail route is confirmed
            break;
        }
      },
    );
  }

  deactivate() {
    if (this.unsubscribeKnowledgeHub) {
      this.unsubscribeKnowledgeHub();
      this.unsubscribeKnowledgeHub = undefined;
    }
    if (this.unsubscribeNavigate) {
      this.unsubscribeNavigate();
      this.unsubscribeNavigate = undefined;
    }
  }
}
